import { FC, useMemo, useState } from 'react';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import useAdminFamilies, { FamilyMember } from '@/api/useAdminFamilies.ts';
import useAdminDeleteUser from '@/api/useAdminDeleteUser.ts';
import DeleteUserModal from '@/components/modals/DeleteUserModal.tsx';
import { TrashIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';

const columnHelper = createColumnHelper<FamilyMember>();

const AdminFamiliesPage: FC = () => {
  const { data: families, isLoading, isError } = useAdminFamilies();
  const navigate = useNavigate();
  const { mutate: deleteUser, isPending: isDeleting, isError: isDeleteError, error: deleteError, reset: resetDelete } = useAdminDeleteUser();
  const [deleteTarget, setDeleteTarget] = useState<FamilyMember | null>(null);

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteUser(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Family',
        cell: (info) => (
          <div className="font-semibold">
            {info.getValue().split('@')[0]}
          </div>
        ),
      }),
      columnHelper.accessor('created_at', {
        header: 'Creation date',
        cell: (info) => dayjs(info.getValue()).format('MMMM DD, YYYY — hh:mm A'),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Details',
        cell: (info) => (
          <button 
            onClick={() => navigate(Routes.ADMIN_FAMILY_DETAILS.replace(':familyId', info.row.original.id))}
            className="text-black hover:underline font-semibold"
          >
            Show details
          </button>
        ),
      }),
      columnHelper.display({
        id: 'delete',
        header: '',
        cell: (info) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              resetDelete();
              setDeleteTarget(info.row.original);
            }}
            className="p-2 text-gray-400 hover:text-[#FF5861] transition-colors cursor-pointer"
            title="Delete family member"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        ),
      }),
    ],
    [navigate, resetDelete]
  );

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lite admin dashboard for daily management</h1>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : isError ? (
        <div className="bg-red-50 rounded-lg p-8 text-center">
          <p className="text-red-500">Error loading families</p>
        </div>
      ) : !families || families.length === 0 ? (
        <div className="bg-[#F6F6F4] rounded-lg flex items-center justify-center py-10 w-full mt-4">
          <p className="text-base font-semibold text-center text-black">No families yet.</p>
        </div>
      ) : (
        <DataTable data={families} columns={columns} className="min-w-[480px]" />
      )}

      <DeleteUserModal
        isOpen={!!deleteTarget}
        onClose={() => isDeleting ? null : setDeleteTarget(null)}
        onConfirm={handleDelete}
        userName={deleteTarget?.email ?? ''}
        userType="family member"
        isLoading={isDeleting}
        error={isDeleteError ? (deleteError as Error)?.message : null}
      />
    </div>
  );
};

export default AdminFamiliesPage;
